// D106: fal-Queue-Status per SSE-STREAM statt Sekunden-Polling — EIN Subrequest hält die
// Verbindung bis zum terminalen Status offen (Cloudflare zählt JEDEN fetch gegen das
// Invocation-Limit; die 1–1,5-s-Poll-Schleifen rissen es bei der vollen Knoten-Kette, D104).
// fal-Doku (Queue-API, verifiziert 2026-06-04): GET `{status_url}/stream` →
// `text/event-stream`; jedes Event = JSON-Statusobjekt im Poll-Format (IN_QUEUE /
// IN_PROGRESS / COMPLETED); der Server hält die Verbindung bis COMPLETED offen.
//
// Vertrag: liefert den TERMINALEN Status (COMPLETED, expliziter `error` ODER ein
// unbekannter Status wie FAILED — die Bewertung übernimmt der Caller mit seiner
// Poll-Semantik) oder `null` bei Timeout/Stream-Ende/HTTP-Fehler → der Caller fällt auf
// das 5-s-Polling zurück (D106-Degradierung). NUR ein Abbruch über das ÄUSSERE `signal`
// (Job-Timeout/Cancel, D27) propagiert als Fehler — kein Fallback-Polling nach Cancel.

const STREAM_TIMEOUT_MS = 180_000; // Default-Deckel = der historische MAX_POLLS-Deckel (~3 min)
// DoS-Guard: ein einzelnes „Event" OHNE Trenner darf den Puffer nicht unbegrenzt wachsen
// lassen → geprüft wird der UNVERARBEITETE Rest-Puffer NACH dem Event-Parsing (ein langer,
// gesunder Stream mit vielen kleinen Events bleibt unbegrenzt streambar). Einheit: UTF-16-
// Codeunits (Codex-P2: ≤ der UTF-8-Bytezahl des Inputs — das Schutzziel ist identisch).
const MAX_EVENT_BUFFER_UNITS = 256 * 1024;

export interface FalTerminalStatus {
  status?: string;
  error?: string;
}

export async function streamFalStatus(opts: {
  /** Validierte fal-`status_url` (`…/status`) — der Stream-Endpoint ist `${statusUrl}/stream`. */
  statusUrl: string;
  apiKey: string;
  signal?: AbortSignal;
  /** Harter Zeitdeckel des Streams (Default 180 s); Ablauf ⇒ `null` (Poll-Fallback). */
  timeoutMs?: number;
}): Promise<FalTerminalStatus | null> {
  const { statusUrl, apiKey, signal } = opts;
  if (signal?.aborted) throw new Error("aborted");
  if (!statusUrl.endsWith("/status")) return null; // unerwartete URL-Form → Poll-Fallback
  const timeoutMs = opts.timeoutMs ?? STREAM_TIMEOUT_MS;

  // Eigener Controller: Timeout ODER das äußere signal brechen fetch/Stream ab; nur der
  // ÄUSSERE Abbruch wirft (Timeout/Netzfehler → null → Poll-Fallback im Caller).
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onOuterAbort = (): void => ctrl.abort();
  signal?.addEventListener("abort", onOuterAbort, { once: true });
  try {
    let res: Response;
    try {
      res = await fetch(`${statusUrl}/stream`, {
        method: "GET",
        headers: { Authorization: `Key ${apiKey}`, Accept: "text/event-stream" },
        signal: ctrl.signal,
      });
    } catch {
      if (signal?.aborted) throw new Error("aborted");
      return null; // Timeout/Netzfehler beim Verbinden → Fallback
    }
    if (!res.ok || !res.body) {
      // Endpoint nicht verfügbar (404/5xx) oder leerer Body → Poll-Fallback; Body aufräumen.
      await res.body?.cancel().catch(() => undefined);
      return null;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    try {
      for (;;) {
        let chunk: ReadableStreamReadResult<Uint8Array>;
        try {
          chunk = await reader.read();
        } catch {
          if (signal?.aborted) throw new Error("aborted");
          return null; // Timeout/Abbruch mitten im Stream → Fallback
        }
        if (chunk.done) return null; // Server schloss ohne terminalen Status → Fallback
        buf += decoder.decode(chunk.value, { stream: true });
        // SSE: Events sind durch Leerzeilen getrennt; `data:`-Zeilen tragen das JSON.
        let sep: number;
        while ((sep = buf.search(/\r?\n\r?\n/)) !== -1) {
          const rawEvent = buf.slice(0, sep);
          buf = buf.slice(sep).replace(/^\r?\n\r?\n/, "");
          const data = rawEvent
            .split(/\r?\n/)
            .filter((l) => l.startsWith("data:"))
            .map((l) => l.slice(5).trim())
            .join("\n");
          if (!data) continue;
          let evt: FalTerminalStatus;
          try {
            evt = JSON.parse(data) as FalTerminalStatus;
          } catch {
            continue; // unparsebares Event ignorieren — der Zeitdeckel begrenzt den Schaden
          }
          if (
            evt.error !== undefined ||
            (typeof evt.status === "string" && evt.status !== "IN_QUEUE" && evt.status !== "IN_PROGRESS")
          )
            return evt;
        }
        // DoS-Guard NACH dem Parsen: nur ein nie-terminiertes Monster-„Event" (kein \n\n)
        // lässt den Rest-Puffer so wachsen — gesunde lange Streams bleiben unbegrenzt.
        if (buf.length > MAX_EVENT_BUFFER_UNITS) return null;
      }
    } finally {
      await reader.cancel().catch(() => undefined);
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onOuterAbort);
  }
}
